import type {ApolloClient} from '@apollo/client';

import {ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import {MCP_TOOL_MAPPING_QUERY} from 'llm/mcp-client-library/MCPToolMappingQuery';
import notifications from 'notifications';
import {INSTALLED_EXTENSION_DEFINITIONS_QUERY} from 'tenants/admin/extensions/InstalledExtensionsQuery';
import {tenantAdminMcpServersSectionPath} from 'tenants/admin/TenantAdminConstants';
import * as WindowLocationUtil from 'util/WindowLocationUtil';

/**
 * MCP setup redirect: after installing an MCP from the extension directory, navigate to the MCP Servers
 * TAC page and open the admin tools modal for the newly-created MCP server config.
 *
 * `serverId` is the installation API id returned in the JSON body of the extension install request for
 * MCP_SERVER installs (`installationApiId`).
 */
export const EXTENSION_MCP_TAC_OPEN_TOOLS_MODAL_EVENT = 'benchling-extensions-mcp-tac-open-tools-modal';

export interface McpTacSetupRedirectToolsModalDetail {
  serverId: string;
  serverName: string;
}

/** Payload from {@link EXTENSION_MCP_TAC_OPEN_TOOLS_MODAL_EVENT}; only our code dispatches it. */
export function detailFromOpenToolsModalEvent(event: Event): McpTacSetupRedirectToolsModalDetail | undefined {
  return (event as CustomEvent<McpTacSetupRedirectToolsModalDetail>).detail ?? undefined;
}

const PENDING_SESSION_STORAGE_KEY = 'benchling.pendingMcpTacSetupRedirectToolsModal';

export function persistPendingMcpTacSetupRedirectToolsModal(
  detail: McpTacSetupRedirectToolsModalDetail
): void {
  window.sessionStorage.setItem(PENDING_SESSION_STORAGE_KEY, JSON.stringify(detail));
}

export function navigateToTacMcpServersAndOpenToolsModal(
  detail: McpTacSetupRedirectToolsModalDetail
): boolean {
  const tacMcpServersUrl = tenantAdminMcpServersSectionPath();
  const currentUrl = WindowLocationUtil.getPathName() + WindowLocationUtil.getSearch();
  const navigated = navigateToURL(tacMcpServersUrl);

  // `navigateToURL` returns false both when navigation is blocked *and* when we're already on the URL.
  if (!navigated && currentUrl !== tacMcpServersUrl) {
    notifications.error('Unable to navigate to Admin Settings. Try again after resolving any prompts.');
    return false;
  }

  // Navigation is asynchronous; the TAC route (and `McpAdminProvider`) may not be mounted yet.
  // Persist + event lets the admin page open the modal reliably after the route renders.
  persistPendingMcpTacSetupRedirectToolsModal(detail);
  window.dispatchEvent(new CustomEvent(EXTENSION_MCP_TAC_OPEN_TOOLS_MODAL_EVENT, {detail}));
  return true;
}

/**
 * After a directory MCP install outside Tenant Admin (e.g. Deep Research), TAC `useQuery`s are not mounted,
 * so nothing refetches. Populate the Apollo cache before navigating so the installed list and MCP tool
 * mapping include the new server when {@link navigateToTacMcpServersAndOpenToolsModal} runs.
 */
export async function refreshCachesAfterMcpDirectoryInstall(client: ApolloClient<object>): Promise<void> {
  await Promise.all([
    client.query({
      query: INSTALLED_EXTENSION_DEFINITIONS_QUERY,
      variables: {extensionType: ExtensionType.MCP_SERVER},
      fetchPolicy: 'network-only',
    }),
    client.query({
      query: MCP_TOOL_MAPPING_QUERY,
      fetchPolicy: 'network-only',
    }),
  ]);
}

/** Same shape as {@link persistPendingMcpTacSetupRedirectToolsModal}; only we write this key. */
export function peekPendingMcpTacSetupRedirectToolsModal(): McpTacSetupRedirectToolsModalDetail | null {
  const raw = window.sessionStorage.getItem(PENDING_SESSION_STORAGE_KEY);
  if (raw == null || raw === '') {
    return null;
  }
  return JSON.parse(raw) as McpTacSetupRedirectToolsModalDetail;
}

export function clearPendingMcpTacSetupRedirectToolsModal(): void {
  window.sessionStorage.removeItem(PENDING_SESSION_STORAGE_KEY);
}
